import React, { Component } from 'react'
import GoogleLogin, { GoogleAuthenticateSuccess } from '#src/components/GoogleLogin'
import { TextField } from '@deal/components'
import { FormContextConsumer, FormContextType } from '#src/components/Path/FormContext'
import { OAuthEmailPathFlowQuestionDefinition } from '#src/components/Path/Question'
import styles from './styles.css'
import { isEmailValid } from '#src/services/email'

interface State {
  isEmailValid: boolean
  isFirstNameValid: boolean
}

class OAuthEmailQuestion extends Component<
  OAuthEmailPathFlowQuestionDefinition<{}> & { context: FormContextType },
  State
> {
  constructor(
    props: OAuthEmailPathFlowQuestionDefinition<{}> & {
      context: FormContextType
    }
  ) {
    super(props)

    const { question } = props

    if (question.oAuthEmailUserInput.email) {
      this.props.onFieldValueChanged({
        email: question.oAuthEmailUserInput.email
      })
    }

    if (question.oAuthEmailUserInput.firstName) {
      this.props.onFieldValueChanged({
        firstName: question.oAuthEmailUserInput.firstName
      })
    }

    this.state = {
      isEmailValid: question.oAuthEmailUserInput.email
        ? isEmailValid(question.oAuthEmailUserInput.email)
        : false,
      isFirstNameValid: question.oAuthEmailUserInput.firstName
        ? this.isFirstNameFieldValid(question.oAuthEmailUserInput.firstName)
        : false
    }

    if (!question.required || (this.state.isEmailValid && this.state.isFirstNameValid)) {
      this.props.onFieldValidityChanged(true)
    }

    this.handleSetEmailValidity = this.handleSetEmailValidity.bind(this)
    this.handleSetFirstNameValidity = this.handleSetFirstNameValidity.bind(this)
    this.handleGoogleSuccess = this.handleGoogleSuccess.bind(this)
    this.handleChange = this.handleChange.bind(this)
  }

  private handleGoogleSuccess(response: GoogleAuthenticateSuccess) {
    const googleIdToken = response.idToken!
    const firstName = response.user.givenName
    const lastName = response.user.familyName
    const email = response.user.email

    this.props.onFieldValueChanged({
      googleIdToken,
      firstName: firstName || '',
      skipFirstName: firstName ? 'YES' : 'NO',
      lastName: lastName || '',
      skipLastName: lastName ? 'YES' : 'NO',
      email: email || ''
    })

    this.handleSetEmailValidity(!!email)
    this.handleSetFirstNameValidity(!!firstName)

    if (this.props.context) {
      this.props.context.onPageSubmitted()
    }
  }

  public handleUpdateQuestionValidity() {
    if (this.state.isEmailValid && this.state.isFirstNameValid) {
      this.props.onFieldValidityChanged(true)
    } else {
      this.props.onFieldValidityChanged(false)
    }
  }

  public handleSetEmailValidity(valid: boolean) {
    this.setState(
      {
        isEmailValid: valid
      },
      this.handleUpdateQuestionValidity
    )
  }

  public handleSetFirstNameValidity(valid: boolean) {
    this.setState(
      {
        isFirstNameValid: valid
      },
      this.handleUpdateQuestionValidity
    )
  }

  private isFirstNameFieldValid(value: string) {
    return value !== '' && value.length > 1
  }

  private handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target

    this.props.onFieldValueChanged({
      [name]: value
    })

    /**
     * If the field is manually entered, check it against our
     * own regex rather than accept it blindly from the oauth
     */
    if (name === 'email') {
      this.handleSetEmailValidity(isEmailValid(value))
    } else if (name === 'firstName') {
      this.handleSetFirstNameValidity(this.isFirstNameFieldValid(value))
    }
  }

  public render() {
    const { question: { oAuthEmailUserInput } } = this.props

    return (
      <span>
        <div className={styles.container}>
          <div className={styles.loginButtons}>
            <div>
              <GoogleLogin
                className={styles.loginButton}
                text="Continue with Google"
                onSuccess={this.handleGoogleSuccess}
              />
            </div>
            <div>
              <p className={styles.questionNote}>or</p>
            </div>
          </div>
          <div className={styles.row}>
            <p className={styles.emailTitle}>Continue with your name and email address</p>

            <div className={styles.inputRow}>
              <TextField
                label=""
                labelHidden={true}
                autoFocus={false}
                type="text"
                name="firstName"
                display="standard"
                onChange={this.handleChange}
                placeholder="First name"
                valid={this.state.isFirstNameValid}
                defaultValue={oAuthEmailUserInput.firstName || undefined}
              />
            </div>

            <div className={styles.inputRow}>
              <TextField
                label=""
                labelHidden={true}
                autoFocus={false}
                type="email"
                name="email"
                display="standard"
                onChange={this.handleChange}
                placeholder="Email"
                valid={this.state.isEmailValid}
                defaultValue={oAuthEmailUserInput.email || undefined}
              />
            </div>
          </div>
        </div>
      </span>
    )
  }
}

const OAuthEmailQuestionWithContext: React.SFC<OAuthEmailPathFlowQuestionDefinition<{}>> = ({
  ...rest
}) => (
  <FormContextConsumer>
    {formData => (formData ? <OAuthEmailQuestion context={formData} {...rest} /> : null)}
  </FormContextConsumer>
)

export default OAuthEmailQuestionWithContext
